import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, Search, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../api/api";
import toast from "react-hot-toast";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const TrackComplaint = () => {
  const pageRef = useRef(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [complaint, setComplaint] = useState(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", { opacity: 0, y: 30, duration: 0.8 });
      gsap.from(".hero-title", { opacity: 0, y: 80, duration: 1.1 });
      gsap.from(".hero-text", { opacity: 0, y: 40, duration: 0.9, delay: 0.25 });
      gsap.from(".track-card", { opacity: 0, y: 60, duration: 1, delay: 0.3 });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const submit = async (e) => {
    e.preventDefault();

    if (!trackingNumber.trim()) {
      toast.error("Enter your complaint reference number");
      return;
    }

    try {
      setLoading(true);
      const res = await api.get(`/complaints/track/${trackingNumber.trim()}`);
      setComplaint(res.data);
    } catch (err) {
      setComplaint(null);
      toast.error(err.cleanMessage || "Complaint reference not found");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main ref={pageRef} className="bg-slate-50 overflow-hidden">
      <section className="relative overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img src={HERO_IMAGE} alt="Track Complaint" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />
          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Complaint Status
              </span>
            </div>

            <h1 className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]" style={{ fontFamily: "'Alegreya Sans', sans-serif" }}>
              Track Complaint
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Use your OGFZA complaint reference number to check the current status of your complaint.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span className="text-[#ff6600]">Track Complaint</span>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16">
        <form onSubmit={submit} className="track-card bg-white rounded-[34px] p-6 md:p-8 border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)]">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center">
              <ShieldCheck size={26} />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-[#06183f]" style={{ fontFamily: "'Alegreya Sans', sans-serif" }}>
                Check Complaint Status
              </h2>
              <p className="text-slate-500 text-sm">
                Example: OGFZA-CMP-2026-000124
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <input
              required
              placeholder="Enter complaint reference number"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              className="flex-1 border border-slate-200 rounded-2xl px-4 py-4 outline-none focus:border-[#06183f]"
            />

            <button disabled={loading} className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-[#06183f] hover:bg-[#ff6600] text-white font-black transition-colors disabled:opacity-60">
              <Search size={18} />
              {loading ? "Checking..." : "Check Status"}
            </button>
          </div>
        </form>

        {complaint && (
          <div className="mt-8 bg-white rounded-[34px] p-6 md:p-8 border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)]">
            <h3 className="text-2xl font-black text-[#06183f] mb-6">
              Complaint Status Result
            </h3>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                ["Reference Number", complaint.tracking_number],
                ["Status", complaint.status?.replaceAll("_", " ")],
                ["Complaint Type", complaint.complaint_type],
                ["Complainant Category", complaint.complainant_category],
                ["Free Zone", complaint.free_zone || "Not specified"],
                ["Assigned Department", complaint.assigned_department || "Pending assignment"],
                ["Date Submitted", complaint.created_at],
                ["Last Updated", complaint.updated_at],
              ].map(([label, value]) => (
                <div key={label} className="bg-slate-50 rounded-2xl p-4">
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-black">{label}</p>
                  <p className="text-[#06183f] font-bold mt-1 capitalize break-words">{value || "Not available"}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 bg-[#06183f]/5 rounded-3xl p-5">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-black">Response Note</p>
              <p className="text-slate-700 mt-2 leading-relaxed">
                {complaint.response_note || "No response note available yet."}
              </p>
            </div>

            {complaint.resolution_summary && (
              <div className="mt-5 bg-green-50 rounded-3xl p-5">
                <p className="text-xs uppercase tracking-widest text-green-700 font-black">Resolution Summary</p>
                <p className="text-green-800 mt-2 leading-relaxed">{complaint.resolution_summary}</p>
              </div>
            )}
          </div>
        )}
      </section>
    </main>
  );
};

export default TrackComplaint;